import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { pageHead } from "../empty";
import type { ScreenContext } from "../../shell/types";

// Settings › Modules (mockup): one card per module (its manifest's, brief 1E-8) with its on/off
// switch, the shared core that's always on, and the Compliance add-on when the company is offered
// it. The 1A-4 rules decide; their messages ("Compliance needs Home Care on", "switch Compliance off first") are shown as they are.
// Brief 1E-12 (mockup v62): a Business picker on each module; a change applies to new invoices,
// claims, payroll runs and card payments, and is in the history. Brief 1E-14: a company with one
// live business sees no pickers; everything uses that business.

const CORE: [string, string, string][] = [
  ["people", "People", "Candidates, workers, caregivers and drivers in one list"],
  ["organization", "Clients and contacts", "Every client, patient, rider and their contacts"],
  ["checklist", "Requirements library", "Used by any module that onboards people"],
  ["file", "Documents", "Uploads, review, secure sending"],
  ["comment", "Messages", "Portal, text, email and calls"],
  ["credit-card", "Billing engine", "Invoices, payments, receivables"],
  ["graph", "Reports and audit", "Down to the line"],
  ["eye", "Portals", "Facility, client, family, rider, candidate"],
];

interface ModulesBody {
  modules: { module: string; on: boolean }[];
  /** Each installed module's card, from its manifest (brief 1E-8). */
  cards: { module: string; name: string; icon: string; blurb: string }[];
  /** The shared inbox feature (brief 1B-5). */
  sharedInbox: boolean;
  /** The live businesses and the one each module runs under (brief 1E-12). */
  businesses: { id: string; name: string }[];
  moduleBusiness: Record<string, string | null>;
}

export async function modulesScreen(host: HTMLElement, ctx: ScreenContext) {
  host.append(pageHead(ctx, "Each module runs on its own. Turn on any mix.", [], null));
  const res = await api<ModulesBody>("GET", "/api/settings/modules");
  if (!res.ok) {
    host.append(
      el("p", { class: "note", role: "alert" }, res.body.error ?? "Modules couldn't be loaded."),
    );
    return;
  }
  let state = res.body.modules;
  let shared = res.body.sharedInbox;
  const businesses = res.body.businesses;
  const moduleBusiness = { ...res.body.moduleBusiness };
  const picks = businesses.length > 1;
  const message = el("div", { class: "note", role: "alert", hidden: true });

  function toggle(module: string, name: string) {
    const on = state.find((m) => m.module === module)?.on ?? false;
    const input = el("input", {
      type: "checkbox",
      // A switch saves at once: not a form (brief 1E-23).
      "data-unsaved": "off",
      id: `mod-${module}`,
      "data-module": module,
      "aria-label": `${name} on`,
      checked: on,
    });
    const word = el("span", {}, on ? "On" : "Off");
    input.addEventListener("change", async () => {
      input.disabled = true;
      const want = input.checked;
      const put = await api<ModulesBody>("PUT", "/api/settings/modules", { module, on: want });
      input.disabled = false;
      if (!put.ok) {
        input.checked = !want;
        message.hidden = false;
        message.replaceChildren(
          el("p", {}, el("b", {}, `${name} wasn't switched: `), put.body.error ?? "try again"),
        );
        return;
      }
      message.hidden = true;
      state = put.body.modules;
      word.textContent = want ? "On" : "Off";
      // The menus are redrawn first, so the toast never says "on" before they show it.
      await ctx.refresh();
      toast(
        `${name} is ${want ? "on" : "off"}`,
        want ? "Its screens are back in the menus" : "Its records are kept",
      );
    });
    return el("label", { class: "switch-l" }, input, word);
  }

  const cards = res.body.cards
    .filter((c) => state.some((m) => m.module === c.module))
    .map((c) =>
      el(
        "section",
        { class: "card modcard", "aria-label": c.name },
        el(
          "div",
          { class: "cardhead" },
          el("span", { class: "modname" }, icon(c.icon), ` ${c.name}`),
          toggle(c.module, c.name),
        ),
        el("span", { class: "small muted" }, c.blurb),
        picks && c.module in moduleBusiness ? businessPicker(c.module, c.name) : null,
      ),
    );

  /** The module's Business picker: the live businesses; a change saves at once. */
  function businessPicker(module: string, name: string) {
    const id = `mb-${module}`;
    const now = moduleBusiness[module] ?? "";
    const input = el(
      "select",
      { id, "data-module-business": module },
      ...(now ? [] : [el("option", { value: "", selected: true }, "Pick a business")]),
      ...businesses.map((b) => el("option", { value: b.id, selected: b.id === now }, b.name)),
    );
    input.addEventListener("change", async () => {
      if (!input.value) return;
      input.disabled = true;
      const put = await api<{ moduleBusiness: Record<string, string | null> }>(
        "PUT",
        "/api/settings/modules/business",
        { module, business: input.value },
      );
      input.disabled = false;
      if (!put.ok) {
        input.value = moduleBusiness[module] ?? "";
        return toast(`${name} wasn't moved`, put.body.error);
      }
      Object.assign(moduleBusiness, put.body.moduleBusiness);
      input.querySelector('option[value=""]')?.remove();
      toast(
        `${name} now runs under ${businesses.find((b) => b.id === input.value)?.name ?? ""}`,
        "New invoices and claims use it; past ones keep theirs",
      );
    });
    return el(
      "div",
      { class: "field", style: "margin:10px 0 0" },
      el("label", { for: id }, "Business"),
      input,
    );
  }

  host.append(
    message,
    el("div", { class: "grid3" }, ...cards),
    ...(picks
      ? [
          el(
            "p",
            { class: "small muted", style: "margin:-4px 0 12px" },
            "Each module's invoices, claims, payroll file and card payments go out under its business.",
          ),
        ]
      : []),
    el(
      "section",
      { class: "card" },
      el("h2", {}, "Shared core (always on)"),
      el(
        "div",
        { class: "grid3", style: "gap:8px" },
        ...CORE.map(([ic, title, sub]) =>
          el(
            "div",
            { class: "ptask", style: "align-items:flex-start" },
            icon(ic),
            el("div", {}, el("b", {}, title), el("div", { class: "small muted" }, sub)),
          ),
        ),
      ),
    ),
  );

  // The shared inbox: a feature of every module, not a module (brief 1B-5, mockup v20).
  const sharedInput = el("input", {
    type: "checkbox",
    // A switch saves at once: not a form (brief 1E-23).
    "data-unsaved": "off",
    id: "feat-shared-inbox",
    "aria-label": "Shared inbox on",
    checked: shared,
  });
  const sharedWord = el("span", {}, shared ? "On" : "Off");
  sharedInput.addEventListener("change", async () => {
    sharedInput.disabled = true;
    const want = sharedInput.checked;
    const put = await api<{ sharedInbox: boolean }>("PUT", "/api/settings/shared-inbox", {
      on: want,
    });
    sharedInput.disabled = false;
    if (!put.ok) {
      sharedInput.checked = !want;
      return toast("The shared inbox wasn't switched", put.body.error);
    }
    shared = put.body.sharedInbox;
    sharedWord.textContent = shared ? "On" : "Off";
    await ctx.refresh();
    toast(
      `Shared inbox is ${shared ? "on" : "off"}`,
      shared ? "The Inbox is back in the top bar" : "Message anyone from their record",
    );
  });
  host.append(
    el(
      "section",
      { class: "card", "aria-label": "Shared inbox" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, "Feature · every module"),
          el("h2", {}, icon("inbox"), " Shared inbox"),
        ),
        el("label", { class: "switch-l" }, sharedInput, sharedWord),
      ),
      el(
        "p",
        { class: "small muted", style: "margin:0" },
        "Off: messages and replies stay on each record. On: one team inbox across every module.",
      ),
    ),
  );

  // Brief 1E-19: Home Care's features, each a switch (a new company starts with them off).
  if (state.some((m) => m.module === "homecare" && m.on)) {
    const slot = el("div");
    host.append(slot);
    void homeCareFeatures(slot);
  }

  if (state.some((m) => m.module === "compliance")) {
    host.append(
      el(
        "section",
        { class: "card", "aria-label": "Compliance" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("div", { class: "eyebrow" }, "Add-on · this company only"),
            el("h2", {}, icon("law"), " Compliance: Maryland RSA (COMAR 10.07.05)"),
          ),
          toggle("compliance", "Compliance"),
        ),
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          "Built from the company's license manual. Needs Home Care on.",
        ),
      ),
    );
  }
}

interface Features {
  canChange: boolean;
  features: { key: string; name: string; blurb: string; on: boolean }[];
}

/** Home Care features: each on or off, saved at once; turning one off keeps its records. */
async function homeCareFeatures(slot: HTMLElement) {
  const res = await api<Features>("GET", "/api/homecare/features");
  if (!res.ok) return;
  const f = res.body;
  slot.replaceChildren(
    el(
      "section",
      { class: "card", "aria-label": "Module features" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("div", { class: "eyebrow" }, "Features · Home Care"),
          el("h2", {}, icon("sliders"), " Home Care features"),
        ),
      ),
      el(
        "div",
        { class: "grid3", style: "gap:8px" },
        ...f.features.map((x) => {
          const input = el("input", {
            type: "checkbox",
            // A switch saves at once: not a form (brief 1E-23).
            "data-unsaved": "off",
            id: `ft-homecare-${x.key}`,
            checked: x.on,
            disabled: !f.canChange,
          });
          input.addEventListener("change", async () => {
            input.disabled = true;
            const want = input.checked;
            const put = await api<Features>("PUT", "/api/homecare/features", {
              key: x.key,
              on: want,
            });
            input.disabled = false;
            if (!put.ok) {
              input.checked = !want;
              return toast(`${x.name} wasn't switched`, put.body.error);
            }
            toast(`${x.name} ${want ? "on" : "off"}`, want ? undefined : "Its records are kept");
          });
          return el(
            "div",
            {},
            el("label", { class: "ptask", for: input.id }, input, ` ${x.name}`),
            el("div", { class: "small muted" }, x.blurb),
          );
        }),
      ),
    ),
  );
}
